import apiCall from "../common/api/apiCall";
import { customISODateString } from "../helpers/preventTimeZone";
import { getPaymentComments } from "../pages/PaymentsView/api/PaymentsAPI";
import { emptyMongoApi } from "./emptyMongoApi";

const pcAPI = emptyMongoApi.injectEndpoints({
    endpoints: (builder) => ({
        getPaymentComments: builder.query({
            queryFn: async (id) => {
                try {
                    if (!id) {
                        return { data: [] };
                    }

                    const result = await getPaymentComments(id);

                    return { data: result };
                } catch (error) {
                    console.error(error);
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
        }),
        addPaymentComment: builder.mutation({
            queryFn: async (newComment) => {
                try {
                    const formatNewComment = {
                        scheduleId: newComment.ScheduleId,
                        propertyId: newComment.PropertyId,
                        contractId: newComment.ContractId,
                        startDate: newComment.StartDate,
                        endDate: newComment.EndDate,
                        comment: newComment.Comment,
                        commentKey: newComment.CommentKey,
                    };

                    if (!Object.values(formatNewComment).every((val) => val)) {
                        throw new Error("Missing required fields for adding payment comment");
                    }

                    const result = await apiCall.post("AddPaymentComment", { ...formatNewComment });
                    return { data: result.data };
                } catch (error) {
                    return { error: { message: error.message || "Failed to add payment comment" } };
                }
            },
            onQueryStarted: async (newComment, { dispatch, queryFulfilled }) => {
                const patchResult = dispatch(
                    pcAPI.util.updateQueryData("getPaymentComments", newComment.CommentKey, (draft) => {
                        const currTop = draft[0];
                        const date = new Date(
                            new Date().getTime() - new Date().getTimezoneOffset() * 60000
                        ).toISOString();

                        draft.unshift({
                            userName: newComment.UserName,
                            comment: newComment.Comment,
                            date: date,
                            fromUser: 1,
                            reminderDate: currTop?.reminderDate ?? null,
                        });
                    })
                );

                const patchResultGridData = dispatch(
                    pcAPI.util.updateQueryData("getGridData", { module: "missingPayments" }, (draft) => {
                        const itemToUpdate = draft.data.find((item) => item.CommentKey === newComment.CommentKey);
                        const topComment = itemToUpdate?.Status[0];
                        const date = topComment ? itemToUpdate.Status[0].reminderDate : null;
                        if (itemToUpdate) {
                            itemToUpdate.Status[0] = {
                                userName: newComment.UserName,
                                comment: newComment.Comment,
                                date: customISODateString(new Date()),
                                fromUser: 1,
                                count: topComment ? Number(itemToUpdate.Status[0].count) + 1 : 1,
                                reminderDate: date,
                            };

                            itemToUpdate.ReminderDate = date;
                        }
                    })
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                    patchResultGridData.undo();
                }
            },
        }),
        updatePaymentReminder: builder.mutation({
            queryFn: async ({ id = null, date = null, comment = "", commentTo = [] }) => {
                try {
                    if (!id) {
                        throw new Error("Missing required fields for updating payment reminder");
                    }

                    const updateComment = {
                        id: id,
                        date: date,
                        module: 7,
                        comment: comment,
                        commentTo: commentTo,
                    };

                    const result = await apiCall.post("UpdateReminder", {
                        ...updateComment,
                    });

                    return { data: result.data };
                } catch (error) {
                    return { error: { message: error.message || "Failed to update payment reminder" } };
                }
            },
            onQueryStarted: async (data, { dispatch, queryFulfilled }) => {
                const safeDate = data.date;
                const comment = data.comment;
                const patchResult = dispatch(
                    pcAPI.util.updateQueryData("getPaymentComments", data.id, (draft) => {
                        if (Array.isArray(draft) && draft.length > 0) {
                            draft[0].reminderDate = safeDate;
                        }
                    })
                );

                const patchResultGridData = dispatch(
                    pcAPI.util.updateQueryData("getGridData", { module: "missingPayments" }, (draft) => {
                        // If data.id is an array, iterate over it
                        const ids = Array.isArray(data.id) ? data.id : [data.id];

                        ids.forEach((id) => {
                            const itemToUpdate = draft.data.find((item) => item.CommentKey === id);
                            if (itemToUpdate) {
                                let newStatus = itemToUpdate.Status[0];

                                if (!safeDate && !newStatus.comment) {
                                    itemToUpdate.Status = [];
                                    itemToUpdate.ReminderDate = null;
                                    return;
                                }

                                if (!newStatus) {
                                    newStatus = {
                                        userName: data.userName ?? "",
                                        comment: comment ?? "",
                                        date: new Date(),
                                        fromUser: 1,
                                        count: 0,
                                        reminderDate: safeDate,
                                    };
                                } else {
                                    newStatus = {
                                        ...newStatus,
                                        date: comment ? new Date() : newStatus.date,
                                        comment: comment ? comment : newStatus?.comment ?? "",
                                        userName: comment ? data.userName : newStatus.userName,
                                        reminderDate: safeDate,
                                        fromUser: comment ? 1 : newStatus.fromUser,
                                    };
                                }

                                itemToUpdate.Status[0] = newStatus;
                                itemToUpdate.ReminderDate = safeDate;
                            }
                        });
                    })
                );

                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                    patchResultGridData.undo();
                }
            },
        }),
    }),
});

export const { useGetPaymentCommentsQuery, useAddPaymentCommentMutation, useUpdatePaymentReminderMutation } = pcAPI;
